import { useContext } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CommandCenterStateProviderContext } from '../context';
import { Command, CustomScheduleModalDataType, CustomScheduleModalModeType, ScheduleData, ScheduleTask } from '../types';
import useUrlParams from './useUrlParams';
import OperateService from '../../../service/OperateService';
import { getApiFailureMessage, showToastMessage } from '../../../utils/commonFunctions';
import useContacts from './useContacts';
import CommandCenterService from '../../../service/CommandCenterService';
import { getInitialFormTaskData, getInitialFormTelecommandData, getUpdateCustomScheduleModalData } from '../utils';
import useOnBoardSchedule from './useOnBoardSchedule';
dayjs.extend(utc)

const useCustomSchedule = () => {
    const context = useContext(CommandCenterStateProviderContext);
    const { mission_id, scenario_id, sat_id } = useUrlParams()
    const { exportOnBoardOperateSchedule } = useOnBoardSchedule()

    if (context === undefined) {
        throw new Error('useCustomSchedule must be used within a CommandCenterStateProvider');
    }
    const { get_all_onboard_schedules } = useContacts()

    const { onboardScheduleDetails,
        setOnboardScheduleDetails,
        customScheduleModalData,
        setCustomScheduleModalData,
        taskPlanDetails,
        setTaskPlanDetails,
        scheduleConfirmationModal,
        setScheduleConfirmationModal
    } = context

    const deleteOnBoardOperateSchedule = (mission_id: string | undefined, scenario_id: string | undefined, schedule_id: string | undefined, type: string) => {
        OperateService.deleteSchedule(mission_id, scenario_id, schedule_id)?.then((res) => {
            if (res?.data) {
                get_all_onboard_schedules()
                showToastMessage('success', res.data.message || `${type} deleted`);
            } else {
                showToastMessage('error', res.data.message);
            }
        }).catch((err) => {
            showToastMessage('error', 'Error in Deleting Schedule');
        }).finally(() => {
            setScheduleConfirmationModal(null)
        })
    }

    const validateScheduleData = async (updatedScheduleData: any, taskId: string) => {
        try {
            const validatedScheduleData = await CommandCenterService.validateScheduleData(updatedScheduleData, mission_id, scenario_id)
            const scheduledTasks: any[] = Array.from(new Map(validatedScheduleData?.data?.data?.scheduledTasks.map((item: ScheduleTask) => [item.taskId, item])).values());
            const selectedTask = scheduledTasks.find((task: ScheduleTask) => task.taskId === taskId) || null
            const isEmpty = !scheduledTasks?.length;
            setCustomScheduleModalData((prev) => {
                return {
                    ...prev!,
                    duration: isEmpty ? null : validatedScheduleData?.data?.data?.duration,
                    startTime: isEmpty ? null : validatedScheduleData?.data?.data?.startTimeISO8601,
                    selectedSchedule: {
                        ...prev?.selectedSchedule,
                        startTime: validatedScheduleData?.data?.data?.startTime,
                        endTime: validatedScheduleData?.data?.data?.endTime,
                        endTimeISO8601: validatedScheduleData?.data?.data?.endTimeISO8601,
                        startTimeISO8601: validatedScheduleData?.data?.data?.startTimeISO8601,
                        duration: validatedScheduleData?.data?.data?.duration,
                        scheduledTasks: scheduledTasks,
                    },
                    scheduledTasks: scheduledTasks,
                    selectedTask: selectedTask,
                    selectedTelecommand: null,
                    formType: isEmpty ? (prev?.scheduleType === 'Build' ? 'add-task' : 'schedule') : prev?.formType!,
                    formData: isEmpty ? {
                        ...prev?.formData!,
                        sourceId: '',
                        source: prev?.formData?.source ?? 'taskPlan',
                        startTime: dayjs().utc().unix(),
                        taskData: {
                            ...getInitialFormTaskData(prev?.scheduleType),
                            time: null,
                            delay: prev?.scheduleType === 'Build' ? 0 : 10
                        }
                    } : prev?.formData!,
                }
            })
        } catch (error) {
            const message = getApiFailureMessage(error, 'Delete')
            showToastMessage('error', message);
        } finally {
            setScheduleConfirmationModal(null)
        }

    }

    const updateSchedule = async (scheduleData: ScheduleData) => {
        try {
            const response = await CommandCenterService.saveEditedSchedule(scheduleData, mission_id, scenario_id, sat_id, scheduleData.scheduleId)
            if (response?.data) {
                get_all_onboard_schedules()
            }
        } catch (error) {
            const message = getApiFailureMessage(error, 'Update')
            showToastMessage('error', message);
        } finally {
            setScheduleConfirmationModal(null)
        }
    }

    const onDeleteTask = async (view: 'readonly' | 'editable', scheduleData: ScheduleData, taskId: string, type: string) => {
        const updatedTaskData = scheduleData?.scheduledTasks?.filter((task: ScheduleTask) => task.taskId !== taskId)
        if (updatedTaskData?.length === 0 && view === 'readonly') {
            deleteOnBoardOperateSchedule(mission_id, scenario_id, scheduleData?.scheduleId, type = 'schedule')
        } else {
            const updatedScheduleData = {
                ...scheduleData,
                scheduledTasks: updatedTaskData
            }
            if (view === 'readonly') {
                await updateSchedule(updatedScheduleData)
                showToastMessage('success', `${type} deleted`);
            } else {
                await validateScheduleData(updatedScheduleData, taskId)
                showToastMessage('success', 'Task deleted successfully');
            }
        }
    }

    const onDeleteCommand = async (view: 'readonly' | 'editable', scheduleData: ScheduleData, taskId: string, commandId: string, type: string) => {
        const selectedTask = scheduleData?.scheduledTasks?.find((task: ScheduleTask) => task.taskId === taskId && task?.commandsList?.find((command: Command) => command.commandId === commandId))
        const updatedCommandData = selectedTask?.commandsList?.filter((command: Command) => command.commandId !== commandId)
        const filterdTasks = scheduleData?.scheduledTasks?.filter((task: ScheduleTask) => task.taskId !== taskId) || []
        if (!selectedTask) return
        const updatedTaskData = {
            ...selectedTask,
            commandsList: updatedCommandData || [],
            duration: (updatedCommandData || []).reduce((acc: number, command: Command) => acc + (command.duration || 0), 0)
        }
        const updatedScheduleData = {
            ...scheduleData,
            scheduledTasks: [
                ...filterdTasks,
                updatedTaskData
            ]
        }
        if (view === 'readonly') {
            await updateSchedule(updatedScheduleData)
            showToastMessage('success', `${type} deleted`);
        } else {
            await validateScheduleData(updatedScheduleData, taskId)
            showToastMessage('success', 'Telecommand deleted successfully');
        }
    }

    const onDelete = () => {
        if (!scheduleConfirmationModal) return
        const { view, type, data } = scheduleConfirmationModal
        if (!data) return
        const { scheduleId, taskId, commandId } = data
        
        const scheduleData = view === 'editable' ? customScheduleModalData?.selectedSchedule : onboardScheduleDetails?.selectedSchedule
        if (type === 'schedule' && scheduleId) {
            setScheduleConfirmationModal(prev => ({ ...prev!, loading: true }))
            deleteOnBoardOperateSchedule(mission_id, scenario_id, scheduleId, type)
        } else if (type === 'task' && taskId && scheduleData) {
            setScheduleConfirmationModal(prev => ({ ...prev!, loading: true }))
            onDeleteTask(view, scheduleData, taskId, type)
        } else if (type === 'telecommand' && commandId && taskId && scheduleData) {
            setScheduleConfirmationModal(prev => ({ ...prev!, loading: true }))
            onDeleteCommand(view, scheduleData, taskId, commandId, type)
        } else {
            const message = !scheduleId ? 'Schedule ID is missing' : !taskId ? 'Task ID is missing' : !commandId ? 'Command ID is missing' : 'Something went wrong'
            showToastMessage('error', message)
        }
    }

    const getDescription = (type: string, name: string) => {
        switch (type) {
            case 'schedule':
                return `Are you sure you want to delete the schedule '${name}'?`
            case 'task':
                return `Deleting '${name}' will remove it from this schedule, you cannot retrieve it once deleted.`
            case 'telecommand':
                return `Deleting '${name}' will remove it from this task and schedule, you cannot retrieve it once deleted.`
            default:
                return `Are you sure you want to delete '${name}'?`
        }

    }

    const onMenuClick = (menu: string, view: string, type: string, data: { name: string, scheduleId: string, taskId?: string, commandId?: string }) => {
        if (menu === 'Delete') {
            setScheduleConfirmationModal({
                open: true,
                name: data.name,
                data: data,
                view: view as 'readonly' | 'editable',
                type: type as 'schedule' | 'task' | 'telecommand',
                mode: 'delete',
                message: `Delete ${type}?`,
                description: getDescription(type, data?.name),
            })
        }
        else if (menu === 'Export') {
            exportOnBoardOperateSchedule(mission_id, scenario_id, data?.scheduleId)
        }
        else {
            const allSchedules = [...(onboardScheduleDetails?.scheduleData?.planned || []), ...(onboardScheduleDetails?.scheduleData?.available || []), ...(onboardScheduleDetails?.scheduleData?.executed || []), ...(onboardScheduleDetails?.scheduleData?.active || [])]
            let selectedSchedule: ScheduleData | null = null
            if (view === 'editable') {
                selectedSchedule = customScheduleModalData?.selectedSchedule || null
                // If selectedSchedule lacks a scheduleName (editable flows may store name at modal level),
                // fall back to customScheduleModalData.name so the name is visible in the UI.
                if (selectedSchedule && !selectedSchedule.scheduleName && customScheduleModalData?.name) {
                    selectedSchedule = { ...selectedSchedule, scheduleName: customScheduleModalData.name } as ScheduleData
                }
            } else {
                selectedSchedule = (data?.scheduleId ? allSchedules.find(s => s.scheduleId === data.scheduleId) || onboardScheduleDetails?.selectedSchedule : onboardScheduleDetails?.selectedSchedule) || null
            }
            const scheduleTasks = (selectedSchedule?.scheduledTasks || []).filter((task: ScheduleTask) => task?.commandsList?.length > 0)
            const selectedTask: ScheduleTask | null = scheduleTasks?.find((task: ScheduleTask) => task.taskId === data?.taskId) || null
            const command: Command | null = selectedTask?.commandsList?.find((command: Command) => command.commandId === data?.commandId) || null

            const updatedType = type === 'task' ? 'update-task' : type === 'telecommand' ? 'update-telecommand' : customScheduleModalData?.scheduleType === 'Generate' ? 'schedule' : 'empty-page'
            if (!selectedSchedule) return;
            setCustomScheduleModalData(prev => {
                const scheduleType = !prev ? 'Generate' : prev?.scheduleType
                const mode = view === 'readonly' ? 'update' : prev?.mode!
                return { ...getUpdateCustomScheduleModalData(mode, selectedSchedule, selectedTask, command, updatedType, scheduleType) }
            })

        }
    }

    const onTaskSelect = (view: 'readonly' | 'editable', task: ScheduleTask) => {
        if (view === 'editable') {
            setCustomScheduleModalData(prev => {
                if (prev?.formType === 'schedule') {
                    return {
                        ...prev,
                        selectedTask: task,
                    }
                }
                if (prev?.formType === 'add-task' || prev?.formType === 'add-telecommand' || prev?.formType === 'empty-page') {
                    return {
                        ...prev!,
                        formType: 'add-task',
                        selectedTask: task,
                        formData: {
                            ...prev?.formData!,
                            taskData: {
                                ...prev?.formData?.taskData!,
                                taskName: '',
                                adhocTaskId: '',
                                taskSource: 'adhocTask',
                                time: Math.round((task?.startTime ?? 0) + 10),
                                delay: 10,
                                timeOption: 'startTime',
                                taskStartTime: task?.startTime,
                                taskEndTime: task?.endTime
                            }
                        },
                        selectedTelecommand: null,
                        scheduleGenerated: false
                    }
                }
                return {
                    ...prev!,
                    selectedTask: task,
                    selectedTelecommand: prev?.selectedTask?.taskId === task.taskId ? prev?.selectedTelecommand : null,
                    formType: prev?.formType ?? (prev?.scheduleType === 'Generate' ? 'schedule' : 'empty-page'),
                    formData: prev?.formData!,
                }
            })
        } else {
            setOnboardScheduleDetails(prev => ({ ...prev!, selectedTask: task, selectedTelecommand: task.commandsList?.[0] || null }))
        }
    }

    const getTaskForCommand = (tasks: ScheduleTask[], command: Command) => {
        return tasks.find((task) => {
            if (command.taskId && task.taskId === command.taskId) return true
            return task.commandsList?.some((item) => item.commandId === command.commandId)
        }) || null
    }

    const onCommandSelect = (view: 'readonly' | 'editable', command: Command) => {
        if (view === 'editable') {
            setCustomScheduleModalData(prev => {
                if (!prev) return prev;
                const scheduleTasks = prev.selectedSchedule?.scheduledTasks || prev.scheduledTasks || []
                const selectedTask = getTaskForCommand(scheduleTasks, command) || prev.selectedTask
                const selectedCommand = selectedTask?.commandsList?.find((item) => item.commandId === command.commandId) || command
                const updatedType = prev.formType === 'empty-page'
                    ? 'update-telecommand'
                    : prev.formType === 'add-task'
                        ? 'add-telecommand'
                        : prev.formType
                const scheduleType = prev?.scheduleType
                const mode = prev?.mode
                // Ensure selectedSchedule has a visible name in editable flows: if the schedule object
                // lacks scheduleName but the modal stores a name at the modal level, use that as fallback.
                const selectedScheduleWithName = prev.selectedSchedule && !prev.selectedSchedule.scheduleName && prev?.name
                    ? { ...prev.selectedSchedule, scheduleName: prev.name }
                    : prev.selectedSchedule;

                const updatedData = getUpdateCustomScheduleModalData(mode, selectedScheduleWithName!, selectedTask, selectedCommand, updatedType, scheduleType);
                return { ...prev!, ...updatedData, selectedTelecommand: selectedCommand };
            });
        } else {
            setOnboardScheduleDetails(prev => ({ ...prev!, selectedTelecommand: command }))
        }
    }

    const addNewTask = async (formData: any) => {
        try {
            const response = await CommandCenterService.onboardScheduleAddTask(formData, mission_id, scenario_id)
            if (response?.data?.data) {
                const data = response?.data?.data
                const scheduledTasks = [...(customScheduleModalData?.scheduledTasks ?? []), response?.data?.data]
                const body = customScheduleModalData?.scheduleType === 'Build' ? {
                    scheduledTasks: scheduledTasks
                } : {
                    ...customScheduleModalData?.selectedSchedule,
                    scheduledTasks: scheduledTasks
                }
                const validatedScheduleData = await CommandCenterService.validateScheduleData(body, mission_id, scenario_id)
                const validatedTaskList: any[] = Array.from(new Map(validatedScheduleData?.data?.data?.scheduledTasks.map((item: ScheduleTask) => [item.taskId, item])).values());
                const validatedTask = validatedTaskList?.find((task: ScheduleTask) => task.taskId === data?.taskId) || data;
                setCustomScheduleModalData((prev) => {
                    return {
                        ...prev!,
                        duration: validatedScheduleData?.data?.data?.duration,
                        startTime: validatedScheduleData?.data?.data?.startTimeISO8601,
                        selectedSchedule: {
                            ...prev?.selectedSchedule,
                            startTime: validatedScheduleData?.data?.data?.startTime,
                            endTime: validatedScheduleData?.data?.data?.endTime,
                            endTimeISO8601: validatedScheduleData?.data?.data?.endTimeISO8601,
                            startTimeISO8601: validatedScheduleData?.data?.data?.startTimeISO8601,
                            duration: validatedScheduleData?.data?.data?.duration,
                            scheduledTasks: validatedTaskList,
                        },
                        scheduledTasks: validatedTaskList,
                        selectedTask: data,
                        selectedTelecommand: null,
                        scheduleGenerated: true,
                        formLoading: false,
                        loading: false,
                        formData: {
                            ...prev?.formData!,
                            taskData: {
                                ...getInitialFormTaskData(prev?.scheduleType),
                                delay: 10,
                                time: Math.round((validatedTask?.startTime ?? 0) + 10),
                                taskStartTime: validatedTask?.startTime,
                                taskEndTime: validatedTask?.endTime
                            }
                        }
                    }
                })

                showToastMessage('success', response?.data?.message || `Task added`);
            }
        } catch (error) {
            const message = getApiFailureMessage(error, 'Generate')
            setCustomScheduleModalData((prev) => ({ ...prev!, formLoading: false }))
            showToastMessage('error', message);
        }
    }

    const addUpdateNewTelecommand = async (formType: string, formData: any, taskId: string, commandId?: string) => {
        try {
            const isUpdateTelecommand = formType === 'update-telecommand'
            const response = await CommandCenterService.onboardScheduleAddCommand(formData, mission_id)
            const responseData = response?.data?.data
            if (responseData) {
                const selectedTask = customScheduleModalData?.scheduledTasks?.find((task: ScheduleTask) => task.taskId === taskId) || null
                const existingCommands = selectedTask?.commandsList || []
                const commandList: Command[] = isUpdateTelecommand
                    ? existingCommands.map((command: Command) => command.commandId === commandId ? responseData : command)
                    : [...existingCommands, responseData]
                const updatedCommandList = isUpdateTelecommand && !existingCommands.some((command: Command) => command.commandId === commandId)
                    ? [...commandList, responseData]
                    : commandList
                const updatedTask = {
                    ...selectedTask,
                    commandsList: updatedCommandList,
                    duration: updatedCommandList.reduce((acc, command) => acc + (command.duration || 0), 0)
                }
                const filterScheduledTasks = customScheduleModalData?.selectedSchedule?.scheduledTasks?.filter((task: ScheduleTask) => task.taskId !== updatedTask.taskId) || []
                const scheduledTasks = [...filterScheduledTasks, updatedTask]
                const body = customScheduleModalData?.scheduleType === 'Build' ? {
                    scheduledTasks: scheduledTasks
                } : {
                    ...customScheduleModalData?.selectedSchedule,
                    scheduledTasks: scheduledTasks
                }
                const validatedScheduleData = await CommandCenterService.validateScheduleData(body, mission_id, scenario_id)
                const validatedTaskList: any[] = Array.from(new Map(validatedScheduleData?.data?.data?.scheduledTasks.map((item: ScheduleTask) => [item.taskId, item])).values());
                const matchedTask = validatedTaskList?.find((task) => task.taskId === taskId);
                const recentlyEditedTelecommand = isUpdateTelecommand && responseData?.commandId ? {
                    commandId: responseData.commandId,
                    taskId,
                    updatedAt: Date.now()
                } : null
                const updatedSelectedSchedule = {
                    duration: validatedScheduleData?.data?.data?.duration,
                    startTime: validatedScheduleData?.data?.data?.startTime,
                    'scheduledTasks': validatedTaskList,
                }

                setCustomScheduleModalData((prev) => ({
                    ...prev,
                    duration: validatedScheduleData?.data?.data?.duration,
                    startTime: validatedScheduleData?.data?.data?.startTimeISO8601,
                    selectedSchedule: { ...prev?.selectedSchedule, ...updatedSelectedSchedule },
                    selectedTask: matchedTask,
                    selectedTelecommand: isUpdateTelecommand ? null : responseData,
                    scheduledTasks: validatedTaskList,
                    formType: isUpdateTelecommand ? 'empty-page' : prev?.formType,
                    scheduleGenerated: prev?.scheduleGenerated ? true : false,
                    recentlyEditedTelecommand,
                    formData: {
                        ...prev?.formData,
                        telecommandData: {
                            ...getInitialFormTelecommandData(),
                            commandId: responseData?.commandId,
                            taskId: taskId,
                            delay: isUpdateTelecommand ? prev?.formData?.telecommandData?.delay : 10,
                            time: isUpdateTelecommand ? prev?.formData?.telecommandData?.time : (prev?.formData?.telecommandData?.time ?? 0) + 10,
                            commandStartTime: responseData?.epoch || null,
                            commandEndTime: responseData?.epoch + responseData?.duration || null,
                        },
                    },
                    formLoading: false,
                } as CustomScheduleModalDataType))
                showToastMessage('success', response?.data?.message || `Telecommand ${isUpdateTelecommand ? 'updated' : 'added'}`);
            }
        } catch (error) {
            const message = getApiFailureMessage(error, 'Generate')
            setCustomScheduleModalData((prev) => ({ ...prev!, formLoading: false }))
            showToastMessage('error', message);
        }
    }

    const generateSchedule = async (formData: any) => {
        try {
            const response = await CommandCenterService.generateOnboardSchedule(formData, mission_id, scenario_id, sat_id)
            if (response?.data?.data) {
                const data = response?.data?.data
                const scheduledTasks: any[] = Array.from(new Map((data?.scheduledTasks || []).map((item: ScheduleTask) => [item.taskId, item])).values());
                const selectedTask = scheduledTasks?.length > 0 ? scheduledTasks?.find(task => task?.commandsList?.length > 0) : null
                setCustomScheduleModalData((prev) => ({
                    ...prev!,
                    formLoading: false,
                    scheduleGenerated: true,
                    generatedFormSnapshot: {
                        source: prev?.formData?.source,
                        sourceId: prev?.formData?.sourceId,
                        startTime: prev?.formData?.startTime,
                        duration: prev?.formData?.duration,
                    },
                    startTime: data?.startTimeISO8601,
                    duration: data?.duration,
                    scheduledTasks: scheduledTasks,
                    selectedSchedule: {
                        ...data,
                        scheduleId: prev?.selectedSchedule?.scheduleId || data?.scheduleId,
                        scheduledTasks: scheduledTasks,
                    },
                    selectedTask: selectedTask,
                    selectedTelecommand: null,
                }))
                showToastMessage('success', response?.data?.message || 'Schedule Generated Successfully');
            }
        } catch (error) {
            const message = getApiFailureMessage(error, 'Generate')
            showToastMessage('error', message);
            setCustomScheduleModalData((prev) => ({
                ...prev!,
                formLoading: false
            }))
        }
    }

    const onSaveSchedule = async (mode: string, data: ScheduleData) => {
        let response
        try {
            if (mode === 'create' || !data?.scheduleId) {
                response = await CommandCenterService.saveScheduleData(data, mission_id, scenario_id, sat_id)
            } else {
                response = await CommandCenterService.saveEditedSchedule(data, mission_id, scenario_id, sat_id, data?.scheduleId)
            }
            if (response?.data?.data) {
                const scheduledTasks: any[] = Array.from(new Map((data?.scheduledTasks || []).map((item: ScheduleTask) => [item.taskId, item])).values());
                const responseData = {
                    ...data,
                    scheduleId: response?.data?.data?.scheduleId,
                    scheduledTasks: scheduledTasks,
                    scheduleName: response?.data?.data?.scheduleName,
                    startTime: response?.data?.data?.startTime,
                    endTime: response?.data?.data?.endTime,
                    endTimeISO8601: response?.data?.data?.endTimeISO8601,
                    startTimeISO8601: response?.data?.data?.startTimeISO8601,
                    duration: response?.data?.data?.duration,
                }
                get_all_onboard_schedules() // TODO: Check if this is not working correctly
                setCustomScheduleModalData(null)
                showToastMessage('success', response?.data?.message || 'Schedule Saved Successfully');
            }
        } catch (error) {
            const message = getApiFailureMessage(error, 'Save')
            showToastMessage('error', message);
            setCustomScheduleModalData((prev) => ({
                ...prev!,
                loading: false
            }))
        }
    }

    return {
        onboardScheduleDetails,
        setOnboardScheduleDetails,
        customScheduleModalData,
        setCustomScheduleModalData,
        taskPlanDetails,
        setTaskPlanDetails,
        scheduleConfirmationModal,
        setScheduleConfirmationModal,
        onMenuClick,
        onDelete,
        addNewTask,
        onTaskSelect,
        onCommandSelect,
        generateSchedule,
        onSaveSchedule,
        addUpdateNewTelecommand
    }
}

export default useCustomSchedule
